// ============================================================
// ANSICHTSSACHE-AGENT – Mitwachsendes Textfeld
// ============================================================
// Ein Feld, das wie eine Zeile beginnt und mit dem Text waechst.
//
// Der Anlass ist die Zeiterfassung: „Woran hast du gearbeitet?"
// ist meistens eine Zeile — und manchmal eine Liste mit
// Spiegelstrichen, weil an einem Tag drei Dinge an derselben
// Sache passiert sind. Ein einzeiliges `input` kann das nicht,
// ein `textarea` mit fester Hoehe von 80 Pixeln macht aus jeder
// Formularzeile einen Block, und ein Ziehgriff in der Ecke ist
// Handarbeit fuer etwas, das die Anwendung selbst weiss.
//
// DIE HOEHE IST GERECHNET, NICHT GERATEN: 11 Pixel Luft oben und
// unten auf eine Zeilenhoehe von 18 ergeben genau 40 — dieselben
// `h-10` wie Eingabefeld und Auswahlfeld daneben. Deshalb steht
// die Zeilenhoehe hier fest und nicht am Schrift-Token: waere sie
// 19, stuende das Feld einen Pixel hoeher als seine Nachbarn, und
// genau das meldet `npm run design:check`.
//
// AUFZAEHLUNGEN FUEHREN SICH SELBST FORT. Wer eine Zeile mit
// „- " beginnt, bekommt beim Zeilenumbruch denselben Strich
// wieder. Eine leere Aufzaehlungszeile beendet die Liste, statt
// einen Strich ins Nichts zu setzen — dasselbe Verhalten wie in
// jedem Editor, den der Nutzer sonst benutzt.
// ============================================================

"use client";

import { forwardRef, useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

/** Eine Zeile misst so viel — siehe Kopf dieser Datei. */
const ZEILE = 18;
const LUFT = 22;

export interface MitwachsfeldProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "rows" | "value" | "onChange"
> {
  value: string;
  onChange: (wert: string) => void;
  /** Ab wie vielen Zeilen das Feld stehenbleibt und rollt. */
  maxZeilen?: number;
}

export const Mitwachsfeld = forwardRef<HTMLTextAreaElement, MitwachsfeldProps>(
  ({ value, onChange, maxZeilen = 6, className, onKeyDown, ...rest }, aussenRef) => {
    const eigenerRef = useRef<HTMLTextAreaElement>(null);
    const feld = (aussenRef as React.RefObject<HTMLTextAreaElement>) ?? eigenerRef;
    const maxHoehe = LUFT + maxZeilen * ZEILE;
    /**
     * Wohin die Schreibmarke nach dem naechsten Zeichnen gehoert.
     *
     * Notwendig, weil das Fortsetzen einer Aufzaehlung den Wert
     * VON AUSSEN setzt: React zeichnet das Feld neu und stellt die
     * Marke ans Ende. Die Korrektur muss deshalb in derselben
     * Runde passieren, in der der neue Wert im DOM landet — ein
     * `requestAnimationFrame` waere zu spaet. Wer schnell tippt,
     * bekommt sonst das naechste Zeichen ans Textende statt hinter
     * den frischen Spiegelstrich (gemessen: aus „Texte" wurde
     * „exte" und ein verirrtes „T" am Ende).
     */
    const zielStelle = useRef<number | null>(null);

    // Vor dem Zeichnen messen, nicht danach: sonst sieht man das
    // Feld fuer einen Bildaufbau in der falschen Hoehe springen.
    useLayoutEffect(() => {
      const el = feld.current;
      if (!el) return;
      el.style.height = "auto";
      const gewuenscht = Math.min(el.scrollHeight, maxHoehe);
      el.style.height = `${Math.max(gewuenscht, LUFT + ZEILE)}px`;
      el.style.overflowY = el.scrollHeight > maxHoehe ? "auto" : "hidden";

      if (zielStelle.current !== null) {
        el.setSelectionRange(zielStelle.current, zielStelle.current);
        zielStelle.current = null;
      }
    }, [value, maxHoehe, feld]);

    function tasten(e: React.KeyboardEvent<HTMLTextAreaElement>) {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key !== "Enter" || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;

      const el = e.currentTarget;
      const stelle = el.selectionStart;
      // Nur bei leerer Auswahl fortsetzen — wer Text markiert hat
      // und Eingabe drueckt, will ihn ersetzen.
      if (stelle !== el.selectionEnd) return;

      const zeilenAnfang = value.lastIndexOf("\n", stelle - 1) + 1;
      const zeile = value.slice(zeilenAnfang, stelle);
      const treffer = /^(\s*)([-•*])(\s+)/.exec(zeile);
      if (!treffer) return;

      e.preventDefault();
      const marke = `${treffer[1]}${treffer[2]}${treffer[3]}`;

      // Leerer Aufzaehlungspunkt: die Liste ist zu Ende. Der
      // Strich verschwindet, statt ins Nichts zu zeigen.
      if (zeile.trim() === treffer[2]) {
        zielStelle.current = zeilenAnfang;
        onChange(value.slice(0, zeilenAnfang) + value.slice(stelle));
        return;
      }

      zielStelle.current = stelle + 1 + marke.length;
      onChange(`${value.slice(0, stelle)}\n${marke}${value.slice(stelle)}`);
    }

    return (
      <textarea
        ref={feld}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={tasten}
        className={cn(
          "border-border bg-bg-primary text-body-sm text-text-primary placeholder:text-text-tertiary",
          "w-full resize-none rounded-lg border px-3 py-[11px] leading-[18px]",
          "duration-fast ease-standard transition-[box-shadow,border-color]",
          "hover:border-border-strong",
          "focus:border-accent/40 focus:ring-accent/30 focus:ring-2 focus:outline-none",
          "disabled:hover:border-border disabled:cursor-not-allowed disabled:opacity-40",
          className,
        )}
        {...rest}
      />
    );
  },
);
Mitwachsfeld.displayName = "Mitwachsfeld";
